import { useMemo } from "react";
import { HeaderAnalyticsProps } from "../../../../components/header-analytics";
import { useTranslation } from "react-i18next";
import { useCategoryData } from "../../../../../FuturebilityCockpit/hooks";
import { Category } from "../../../../../FuturebilityCockpit/types";
import { CubeSideName } from "../../../../../FuturebilityCockpit/components/Сube/types";
import { ParticipationHeaderProps } from "./../types";
import {
  calculateQuestionProgress,
  calculateTopicProgressForAnalytics,
} from "../utils";
import { usePreviousValueWhileSaving } from "../usePreviousValueWhileSaving";

export function useAnalyticsHeaderProps({
  publicTest,
  currentSet,
  currentItemIndex,
  isSaving,
}: ParticipationHeaderProps): HeaderAnalyticsProps {
  const { t } = useTranslation();

  const categoryName = useMemo(() => {
    if (currentSet.category === Category.Market) {
      return t("frontend.test.category.market", "Market");
    } else if (currentSet.category === Category.Leadership) {
      return t("frontend.test.category.leadership", "Leadership");
    } else if (currentSet.category === Category.Organization) {
      return t("frontend.test.category.organization", "Organization");
    } else if (currentSet.category === Category.Team) {
      return t("frontend.test.category.team", "Team");
    } else {
      return "N/A";
    }
  }, [currentSet.category, t]);

  const isMarket = currentSet.category === Category.Market;

  const currentCategoryData = useCategoryData({
    currentTestId: publicTest._id,
    category: currentSet.category as Category,
    selectedFilters: undefined,
    rawData: {
      testSets: publicTest.sets,
      allEntries: publicTest.entries,
      client: publicTest.client,
      //@ts-ignore
      tests: [publicTest],
      dynamicTestSets: [],
    },
  });

  const { currentQuestion, totalQuestions } = calculateQuestionProgress(
    currentSet.items,
    currentItemIndex
  );

  let averageScore = 0;
  let topicScore = 0;
  let totalProgress;
  let topicProgress = 0;

  if (isMarket) {
    totalProgress = ((currentQuestion - 1) / totalQuestions) * 100;
  } else {
    // Find the index of the current test set
    const currentSetIndex = publicTest.sets.findIndex(
      ts => ts._id === currentSet._id
    );
    const uncompletedTestSetsCount =
      currentSetIndex >= 0
        ? publicTest.sets.length - currentSetIndex
        : publicTest.sets.length;

    const progressData = calculateTopicProgressForAnalytics(
      publicTest.sets.length,
      uncompletedTestSetsCount,
      currentQuestion,
      totalQuestions
    );
    totalProgress = progressData.totalProgress;
    topicProgress = progressData.topicProgress;
    averageScore = currentCategoryData.categoryAverageScore.averageScore;
    topicScore =
      currentCategoryData.categoryValuesPerSide[
        (currentSet.info.cubeSide as CubeSideName) ?? 1
      ].averagePercentageScore;
  }

  // Preserve the topicProgress value while saving to prevent UI flickering
  const effectiveTopicProgress = usePreviousValueWhileSaving(
    topicProgress,
    isSaving
  );

  return {
    averageScore,
    topicScore,
    totalProgress,
    topicProgress: effectiveTopicProgress,
    currentQuestion,
    totalQuestions,
    categoryName,
    topicName: currentSet.info.name,
    isMarket,
  };
}
